import { io } from 'socket.io-client';
import { URL } from '../components/BaseUrl';
import { decryptToken } from '../Utlis/Encryption';

/**
 * Socket Service for real-time messaging
 */
class SocketService {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
  }

  /**
   * Connect to socket server
   * @param {string} token - JWT token (encrypted or decrypted)
   */
  connect(token = null) {
    // Get token from localStorage if not provided
    if (!token) {
      const encryptedToken = localStorage.getItem('token');
      if (!encryptedToken) {
        console.error('No token found for socket connection');
        return;
      }
      token = decryptToken(encryptedToken);
    }

    // Disconnect existing connection if any
    if (this.socket) {
      this.disconnect();
    }

    // Connect to socket server
    this.socket = io(URL, {
      auth: {
        token: token,
      },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: this.maxReconnectAttempts,
    });

    // Connection event handlers
    this.socket.on('connect', () => {
      console.log('Socket connected:', this.socket.id);
      this.isConnected = true;
      this.reconnectAttempts = 0;
      
      // Authenticate after connection
      this.socket.emit('authenticate', { token: token });
    });

    this.socket.on('authenticated', (data) => {
      console.log('Socket authenticated:', data);
      // Join user room after authentication
      if (data.user?.id) {
        this.socket.emit('join_user_room', data.user.id);
      }
      // Update online status
      this.socket.emit('update_online_status', { isOnline: true });
    });

    this.socket.on('auth_error', (error) => {
      console.error('Socket authentication failed:', error);
    });

    this.socket.on('room_joined', (data) => {
      console.log('Joined room:', data);
    });

    this.socket.on('disconnect', (reason) => {
      console.log('Socket disconnected:', reason);
      this.isConnected = false;
    });

    this.socket.on('connect_error', (error) => {
      console.error('Socket connection error:', error);
      this.reconnectAttempts++;
      if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        console.error('Max reconnection attempts reached');
      }
    });

    this.socket.on('reconnect', (attemptNumber) => {
      console.log('Socket reconnected after', attemptNumber, 'attempts');
      this.reconnectAttempts = 0;
      // Re-authenticate after reconnection
      this.socket.emit('authenticate', { token: token });
    });

    this.socket.on('reconnect_error', (error) => {
      console.error('Socket reconnection error:', error);
    });

    this.socket.on('reconnect_failed', () => {
      console.error('Socket reconnection failed');
    });
  }

  /**
   * Disconnect from socket server
   */
  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
    }
  }

  /**
   * Join a thread room
   * @param {number} threadId - Thread ID
   */
  joinThreadRoom(threadId) {
    if (this.socket && this.isConnected) {
      this.socket.emit('join_thread_room', { threadId });
      console.log('Joined thread room:', threadId);
    } else {
      console.warn('Socket not connected. Cannot join thread room.');
    }
  }

  /**
   * Leave a thread room
   * @param {number} threadId - Thread ID
   */
  leaveThreadRoom(threadId) {
    if (this.socket && this.isConnected) {
      this.socket.emit('leave_thread_room', { threadId });
      console.log('Left thread room:', threadId);
    }
  }

  /**
   * Send a message via socket
   * @param {Object} messageData - Message data
   * @param {number} messageData.threadId - Thread ID
   * @param {number} messageData.receiverId - Receiver ID (optional if threadId provided)
   * @param {string} messageData.content - Message content
   * @param {string} messageData.messageType - Message type (text, image, pdf, audio)
   * @param {string} messageData.tempId - Temporary ID for optimistic updates (optional)
   */
  sendMessage(messageData) {
    if (this.socket && this.isConnected) {
      this.socket.emit('send_message', messageData);
      console.log('Message sent via socket:', messageData);
    } else {
      console.warn('Socket not connected. Cannot send message.');
    }
  }

  /**
   * Listen for incoming messages
   * @param {Function} callback - Callback function to handle received messages
   */
  onMessage(callback) {
    if (this.socket) {
      this.socket.on('receive_message', (data) => {
        console.log('Received message:', data);
        callback(data);
      });
    }
  }

  /**
   * Remove message listener
   */
  offMessage() {
    if (this.socket) {
      this.socket.off('receive_message');
    }
  }

  /**
   * Listen for message sent confirmation
   * @param {Function} callback - Callback function to handle message sent confirmation
   */
  onMessageSent(callback) {
    if (this.socket) {
      this.socket.on('message_sent', (data) => {
        console.log('Message sent confirmation:', data);
        callback(data);
      });
    }
  }

  /**
   * Remove message sent listener
   */
  offMessageSent() {
    if (this.socket) {
      this.socket.off('message_sent');
    }
  }

  /**
   * Listen for unread count updates
   * @param {Function} callback - Callback function to handle unread count updates
   */
  onUnreadCountUpdate(callback) {
    if (this.socket) {
      this.socket.on('unread_count_updated', (data) => {
        console.log('Unread count updated:', data);
        callback(data);
      });
    }
  }

  /**
   * Remove unread count listener
   */
  offUnreadCountUpdate() {
    if (this.socket) {
      this.socket.off('unread_count_updated');
    }
  }

  /**
   * Listen for thread read status updates
   * @param {Function} callback - Callback function to handle thread read updates
   */
  onThreadRead(callback) {
    if (this.socket) {
      this.socket.on('thread_read', (data) => {
        console.log('Thread read status:', data);
        callback(data);
      });
    }
  }

  /**
   * Remove thread read listener
   */
  offThreadRead() {
    if (this.socket) {
      this.socket.off('thread_read');
    }
  }

  /**
   * Join user's personal room
   * @param {number} userId - User ID
   */
  joinUserRoom(userId) {
    if (this.socket && this.isConnected) {
      this.socket.emit('join_user_room', userId);
      console.log('Joined user room:', userId);
    }
  }

  /**
   * Get user statuses
   */
  getUserStatuses() {
    if (this.socket && this.isConnected) {
      this.socket.emit('get_user_statuses');
      console.log('Requested user statuses');
    }
  }

  /**
   * Update online status
   * @param {boolean} isOnline - Online status
   */
  updateOnlineStatus(isOnline) {
    if (this.socket && this.isConnected) {
      this.socket.emit('update_online_status', { isOnline });
      console.log('Updated online status:', isOnline);
    }
  }

  /**
   * Listen for user status changes
   * @param {Function} callback - Callback function
   */
  onUserStatusChange(callback) {
    if (this.socket) {
      this.socket.on('user_status_change', (data) => {
        console.log('User status changed:', data);
        callback(data);
      });
    }
  }

  /**
   * Remove user status change listener
   */
  offUserStatusChange() {
    if (this.socket) {
      this.socket.off('user_status_change');
    }
  }

  /**
   * Listen for user statuses list
   * @param {Function} callback - Callback function
   */
  onUserStatusesList(callback) {
    if (this.socket) {
      this.socket.on('user_statuses_list', (data) => {
        console.log('User statuses list:', data);
        callback(data);
      });
    }
  }

  /**
   * Remove user statuses list listener
   */
  offUserStatusesList() {
    if (this.socket) {
      this.socket.off('user_statuses_list');
    }
  }

  /**
   * Listen for new_message event (alternative to receive_message)
   * @param {Function} callback - Callback function
   */
  onNewMessage(callback) {
    if (this.socket) {
      this.socket.on('new_message', (data) => {
        console.log('New message received:', data);
        // Transform to receive_message format
        callback(data);
      });
    }
  }

  /**
   * Remove new message listener
   */
  offNewMessage() {
    if (this.socket) {
      this.socket.off('new_message');
    }
  }

  /**
   * Get socket connection status
   * @returns {boolean} Connection status
   */
  getConnectionStatus() {
    return this.isConnected && this.socket?.connected;
  }

  /**
   * Get socket instance (for advanced usage)
   * @returns {Socket|null} Socket instance
   */
  getSocket() {
    return this.socket;
  }
}

// Export singleton instance
export default new SocketService();

